import React, { useEffect, useMemo, useState } from 'react';
import type {
  UserAdminCreateRequest,
  UserAdminDTO,
  UserAdminUpdateRequest,
  UserRole,
  UserStatus,
} from '../services/userAdminService';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import councilorService, { type CouncilorDTO } from '../services/councilorService';
import userAdminService, { type ProcuradorJuridicoDTO } from '../services/userAdminService';
import { UI_THEME } from '../ui/theme';
import { useAuth } from '../context/AuthContext';
import { FIELD_CLASS } from './ui/fieldStyles';
import { buttonClassName } from './ui/buttonStyles';
import { toUpperText } from '../utils/formatters';

interface SecretariaDTO { id: number; codigo: string; nome: string; }

const normalizeDocument = (doc: string) => {
  const digits = doc.replace(/\D/g, '');
  return digits.slice(0, 14);
};

const CARGO_OPTIONS: { label: string; value: string; suggestRole?: string }[] = [
  { label: 'Analista de Orçamento', value: 'Analista de Orçamento', suggestRole: 'ORCAMENTO' },
  { label: 'Técnico de Orçamento', value: 'Técnico de Orçamento', suggestRole: 'ORCAMENTO' },
  { label: 'Coordenador de Orçamento', value: 'Coordenador de Orçamento', suggestRole: 'ORCAMENTO' },
  { label: 'Servidor de Secretaria', value: 'Servidor de Secretaria', suggestRole: 'SECRETARIA' },
  { label: 'Técnico de Secretaria', value: 'Técnico de Secretaria', suggestRole: 'SECRETARIA' },
  { label: 'Analista de Convênios', value: 'Analista de Convênios', suggestRole: 'CONVENIOS' },
  { label: 'Técnico de Convênios', value: 'Técnico de Convênios', suggestRole: 'CONVENIOS' },
  { label: 'Assessor Jurídico', value: 'Assessor Jurídico', suggestRole: 'JURIDICO' },
  { label: 'Procurador', value: 'Procurador', suggestRole: 'JURIDICO' },
  { label: 'Advogado', value: 'Advogado', suggestRole: 'JURIDICO' },
  { label: 'Auxiliar de Procurador', value: 'Auxiliar de Procurador', suggestRole: 'JURIDICO_AUXILIAR' },
  { label: 'Gestor Jurídico', value: 'Gestor Jurídico', suggestRole: 'GESTOR_JURIDICO' },
  { label: 'Gestor de Convênios', value: 'Gestor de Convênios', suggestRole: 'SECRETARIA' },
  { label: 'Coordenador de Emendas', value: 'Coordenador de Emendas', suggestRole: 'SECRETARIA' },
  { label: 'Secretário Municipal', value: 'Secretário Municipal', suggestRole: 'SECRETARIA' },
  { label: 'Operador de Sistema', value: 'Operador de Sistema', suggestRole: 'OPERADOR' },
  { label: 'Assistente Administrativo', value: 'Assistente Administrativo', suggestRole: 'OPERADOR' },
  { label: 'Técnico Administrativo', value: 'Técnico Administrativo', suggestRole: 'OPERADOR' },
  { label: 'Prefeito', value: 'Prefeito' },
  { label: 'Vice-Prefeito', value: 'Vice-Prefeito' },
  { label: 'Vereador', value: 'Vereador' },
  { label: 'Outro', value: '' },
];

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  user?: UserAdminDTO | null;
  onClose: () => void;
  onCreate: (payload: UserAdminCreateRequest) => Promise<void>;
  onUpdate: (id: number, payload: UserAdminUpdateRequest) => Promise<void>;
};

const UserFormModal: React.FC<Props> = ({ open, mode, user, onClose, onCreate, onUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // O perfil ADMIN não pode ser atribuído por aqui. A opção só aparece — desabilitada —
  // para quem já é ADMIN, de modo que o select exiba o perfil correto ao editar;
  // sem isso o campo ficaria em branco e um save acidental rebaixaria o usuário.
  const isCurrentlyAdmin = mode === 'edit' && (user?.role ?? '') === 'ADMIN';

  // Definir a senha de outro usuário é privilégio de ADMIN. Na criação o campo é
  // obrigatório, então o formulário inteiro fica restrito a ADMIN.
  const { isAdmin } = useAuth();
  const canSetPassword = isAdmin();

  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const inputClass = `${FIELD_CLASS} mt-1`;
  const labelClass = 'block text-sm font-medium text-slate-700 dark:text-slate-300';
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  const { data: secretarias = [] } = useQuery<SecretariaDTO[]>({
    queryKey: ['secretarias-municipais'],
    queryFn: async () => {
      const { data } = await api.get('/secretarias-municipais');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: parlamentares = [], isLoading: parlamentaresLoading } = useQuery<CouncilorDTO[]>({
    queryKey: ['councilors'],
    queryFn: () => councilorService.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: procuradoresSupervisores = [] } = useQuery<ProcuradorJuridicoDTO[]>({
    queryKey: ['procuradores-juridicos'],
    queryFn: () => userAdminService.listProcuradoresJuridicos(),
    staleTime: 5 * 60 * 1000,
    select: (rows) => rows.filter((r) =>
      (r.role === 'JURIDICO' || r.role === undefined)
      && (mode !== 'edit' || r.id !== user?.id)),
  });

  const initial = useMemo(() => {
    if (mode === 'edit' && user) {
      return {
        nomeCompleto: user.nomeCompleto ?? '',
        document: user.documento ?? '',
        email: user.email ?? '',
        telefone: user.telefone ?? '',
        cargoFuncao: user.cargoFuncao ?? '',
        secretaria: user.secretaria ?? '',
        parlamentarId: user.parlamentarId ?? '',
        procuradorSupervisorId: user.procuradorSupervisorId != null ? String(user.procuradorSupervisorId) : '',
        status: user.status ?? 'ATIVO',
        role: user.role ?? 'OPERADOR',
        gestorParceria: user.gestorParceria ?? false,
        operadorSecretaria: user.operadorSecretaria ?? false,
        password: '',
      };
    }
    return {
      nomeCompleto: '',
      document: '',
      email: '',
      telefone: '',
      cargoFuncao: '',
      secretaria: '',
      parlamentarId: '',
      procuradorSupervisorId: '',
      status: 'ATIVO' as UserStatus,
      role: 'OPERADOR' as UserRole,
      gestorParceria: false,
      operadorSecretaria: false,
      password: '',
    };
  }, [mode, user]);

  const [form, setForm] = useState(initial);
  // Secretaria só é informada para o perfil Secretaria ou para o operador de secretaria.
  const temSecretaria = form.role === 'SECRETARIA' || (form.role === 'OPERADOR' && form.operadorSecretaria);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm(initial);
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    setError(null);

    if (!form.nomeCompleto.trim()) return setError('Nome completo é obrigatório');
    if (!form.email.trim()) return setError('E-mail é obrigatório');

    const docDigits = normalizeDocument(form.document);
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      return setError('Documento deve conter 11 dígitos (CPF) ou 14 dígitos (CNPJ)');
    }

    if (form.role === 'SECRETARIA' && !form.secretaria.trim()) {
      return setError('Selecione a secretaria para usuários com perfil Secretaria.');
    }
    if (form.role === 'OPERADOR' && form.operadorSecretaria && !form.secretaria.trim()) {
      return setError('Selecione a secretaria do operador de secretaria.');
    }
    if (form.role === 'VEREADOR' && !form.parlamentarId.trim()) {
      return setError('Selecione o parlamentar para usuários com perfil Parlamentar.');
    }
    if (form.role === 'JURIDICO_AUXILIAR' && !form.procuradorSupervisorId.trim()) {
      return setError('Selecione o procurador supervisor para usuários com perfil Auxiliar da Procuradoria.');
    }

    try {
      setSaving(true);
      if (mode === 'create') {
        if (!form.password.trim()) return setError('Senha é obrigatória');
        const payload: UserAdminCreateRequest = {
          nomeCompleto: form.nomeCompleto.trim(),
          documento: docDigits,
          email: form.email.trim(),
          telefone: (form.telefone || '').trim() || undefined,
          cargoFuncao: (form.cargoFuncao || '').trim() || undefined,
          secretaria: temSecretaria ? form.secretaria.trim() : undefined,
          parlamentarId: form.role === 'VEREADOR' ? form.parlamentarId.trim() : undefined,
          procuradorSupervisorId: form.role === 'JURIDICO_AUXILIAR' ? Number(form.procuradorSupervisorId) : undefined,
          status: form.status,
          role: form.role,
          gestorParceria: form.role === 'SECRETARIA' ? form.gestorParceria : undefined,
          operadorSecretaria: form.role === 'OPERADOR' ? form.operadorSecretaria : undefined,
          password: form.password,
        };
        await onCreate(payload);
      } else if (mode === 'edit' && user) {
        const payload: UserAdminUpdateRequest = {
          nomeCompleto: form.nomeCompleto.trim(),
          documento: docDigits,
          email: form.email.trim(),
          telefone: (form.telefone || '').trim() || undefined,
          cargoFuncao: (form.cargoFuncao || '').trim() || undefined,
          secretaria: temSecretaria ? form.secretaria.trim() : undefined,
          parlamentarId: form.role === 'VEREADOR' ? form.parlamentarId.trim() : '',
          procuradorSupervisorId: form.role === 'JURIDICO_AUXILIAR' ? Number(form.procuradorSupervisorId) : undefined,
          status: form.status,
          role: form.role,
          gestorParceria: form.role === 'SECRETARIA' ? form.gestorParceria : false,
          operadorSecretaria: form.role === 'OPERADOR' ? form.operadorSecretaria : false,
        };
        if (form.password.trim()) payload.password = form.password;
        await onUpdate(user.id, payload);
      }
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>Usuário</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {mode === 'create' ? 'Novo usuário' : 'Editar usuário'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800" aria-label="Fechar">&times;</button>
        </div>

        <div className="max-h-[75vh] space-y-4 overflow-y-auto p-6">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">{error}</div>}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Nome completo</label>
              <input value={form.nomeCompleto} onChange={(e) => setForm((p) => ({ ...p, nomeCompleto: toUpperText(e.target.value) }))} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Documento (CPF ou CNPJ)</label>
              <input value={form.document} onChange={(e) => setForm((p) => ({ ...p, document: e.target.value }))} className={inputClass} placeholder="000.000.000-00" />
            </div>
            <div>
              <label className={labelClass}>E-mail</label>
              <input value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Telefone</label>
              <input value={form.telefone} onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Cargo / Função</label>
              <select
                value={form.cargoFuncao}
                onChange={(e) => {
                  const selected = CARGO_OPTIONS.find((o) => o.value === e.target.value);
                  setForm((p) => ({
                    ...p,
                    cargoFuncao: e.target.value,
                    ...(selected?.suggestRole ? { role: selected.suggestRole as UserRole } : {}),
                  }));
                }}
                className={inputClass}
              >
                <option value="">Selecione um cargo...</option>
                {CARGO_OPTIONS.map((o) => (
                  <option key={o.value || 'outro'} value={o.value}>{o.label}</option>
                ))}
              </select>
              {form.cargoFuncao && CARGO_OPTIONS.find((o) => o.value === form.cargoFuncao)?.suggestRole && (
                <p className="mt-1 text-xs text-blue-600 dark:text-blue-300">
                  Perfil sugerido: <strong>{CARGO_OPTIONS.find((o) => o.value === form.cargoFuncao)?.suggestRole}</strong>
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>Perfil</label>
              <select
                value={form.role}
                onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as UserRole, secretaria: '', gestorParceria: false, operadorSecretaria: false }))}
                className={inputClass}
              >
                <optgroup label="Workflow de Emendas">
                  <option value="ORCAMENTO">Orçamento</option>
                  <option value="SECRETARIA">Secretaria</option>
                  <option value="CONVENIOS">Convênios</option>
                  {isCurrentlyAdmin && <option value="ADMIN" disabled>Administrador</option>}
                </optgroup>
                <optgroup label="Gestão Institucional">
                  <option value="OPERADOR">Operador</option>
                  <option value="GESTOR_JURIDICO">Gestor Jurídico</option>
                  <option value="ANALISTA">Analista</option>
                  <option value="JURIDICO">Procuradoria Geral Municipal</option>
                  <option value="JURIDICO_AUXILIAR">Auxiliar da Procuradoria (PGM)</option>
                </optgroup>
                <optgroup label="Câmara Municipal">
                  <option value="CAMARA">Câmara Municipal</option>
                  <option value="VEREADOR">Parlamentar</option>
                </optgroup>
              </select>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {isCurrentlyAdmin
                  ? 'O perfil Administrador não pode ser alterado por esta tela.'
                  : 'O perfil Administrador não é atribuível por esta tela.'}
              </p>
            </div>

            {form.role === 'SECRETARIA' && (
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Secretaria <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.secretaria}
                  onChange={(e) => setForm((p) => ({ ...p, secretaria: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">Selecione a secretaria...</option>
                  {secretarias.map((s) => (
                    <option key={s.id} value={s.nome}>{s.nome}</option>
                  ))}
                </select>
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.gestorParceria}
                    onChange={(e) => setForm((p) => ({ ...p, gestorParceria: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  É Gestor da Parceria
                </label>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Acompanha a execução dos planos de trabalho vinculados a esta secretaria.
                </p>
              </div>
            )}

            {form.role === 'OPERADOR' && (
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.operadorSecretaria}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      operadorSecretaria: e.target.checked,
                      secretaria: e.target.checked ? p.secretaria : '',
                    }))}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  É operador de uma secretaria
                </label>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Tem as mesmas funções de Operador no sistema; a secretaria aparece no topo da tela.
                </p>
                {form.operadorSecretaria && (
                  <div className="mt-3">
                    <label className={labelClass}>
                      Secretaria <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.secretaria}
                      onChange={(e) => setForm((p) => ({ ...p, secretaria: e.target.value }))}
                      className={inputClass}
                    >
                      <option value="">Selecione a secretaria...</option>
                      {secretarias.map((sec) => (
                        <option key={sec.id} value={sec.nome}>{sec.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {form.role === 'JURIDICO_AUXILIAR' && (
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Procurador supervisor <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.procuradorSupervisorId}
                  onChange={(e) => setForm((p) => ({ ...p, procuradorSupervisorId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">Selecione o procurador supervisor...</option>
                  {procuradoresSupervisores.map((proc) => (
                    <option key={proc.id} value={proc.id}>
                      {proc.nomeCompleto || proc.email}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  O auxiliar elabora os pareceres em nome deste procurador, que aprova e conclui a etapa.
                </p>
              </div>
            )}

            {form.role === 'VEREADOR' && (
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Parlamentar <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.parlamentarId}
                  onChange={(e) => setForm((p) => ({ ...p, parlamentarId: e.target.value }))}
                  disabled={parlamentaresLoading}
                  className={inputClass}
                >
                  <option value="">
                    {parlamentaresLoading ? 'Carregando parlamentares...' : 'Selecione o parlamentar...'}
                  </option>
                  {parlamentares.map((parlamentar) => (
                    <option key={parlamentar.councilorId} value={parlamentar.councilorId}>
                      {parlamentar.fullName}
                      {parlamentar.politicalParty ? ` (${parlamentar.politicalParty})` : ''}
                      {` - ${parlamentar.councilorId}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className={labelClass}>Situação</label>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as UserStatus }))} className={inputClass}>
                <option value="PENDENTE">Pendente</option>
                <option value="ATIVO">Ativo</option>
                <option value="INATIVO">Inativo</option>
                <option value="BLOQUEADO">Bloqueado</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>{mode === 'create' ? 'Senha' : 'Senha (opcional)'}</label>
              {canSetPassword ? (
                <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className={inputClass} />
              ) : (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Somente administradores podem definir ou redefinir a senha de um usuário.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
          <button type="button" onClick={onClose} className={`${secondaryButtonClass}`}>Cancelar</button>
          <button type="button" disabled={saving} onClick={submit} className={`${primaryButtonClass}`}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;
